import { useEffect, useState } from "react";
import { subscribeToPatientProgress, subscribeToPatientErrors } from "../../services/patientService";

/**
 * @typedef {Object} PatientProgressData
 * @property {string} [step]
 * @property {boolean} [seg_done]
 * @property {boolean} [seg_validated]
 * @property {boolean} [model_exported]
 */

/**
 * Ordered pipeline steps, single source of truth for step order/labels/percentages.
 */
export const STEP_CONFIG = [
    { key: 'upload',                 label: 'Uploading...',                shortLabel: 'Upload',        errorLabel: 'Upload',                 percentage: 10 },
    { key: 'preprocess',             label: 'Processing...',               shortLabel: 'Processing',    errorLabel: 'Preprocess',              percentage: 20 },
    { key: 'artefacts',              label: 'Quality check...',            shortLabel: 'Artefacts',     errorLabel: 
    'Artefacts Check',         percentage: 30},
    { key: 'segmentation',           label: 'Segmentation in progress...', shortLabel: 'Segmentation',  errorLabel: 'Segmentation',            percentage: 40 },
    { key: 'segmentation_done',      label: 'Awaiting corrections',        shortLabel: 'Corrections',   errorLabel: 'Segmentation Done',       percentage: 60 },
    { key: 'segmentation_validated', label: 'Building model...',           shortLabel: 'Model',         errorLabel: 'Segmentation Validated',  percentage: 80 },
    { key: 'model_exported',         label: 'Process complete',            shortLabel: 'Complete',      errorLabel: 'Model Exported',          percentage: 100 },
];

/**
 * Maps a pipeline `step` key to its index in STEP_CONFIG, for driving a Stepper's
 * `activeStep`. Returns -1 when the key is unknown/empty (e.g. status not yet resolved).
 * @param {string} currentStepKey
 * @returns {number}
 */
export function deriveActiveStep(currentStepKey) {
    return STEP_CONFIG.findIndex((step) => step.key === currentStepKey);
}

/**
 * Human-readable step name for error copy (e.g. ProcessingErrorDialog), derived
 * from STEP_CONFIG's `errorLabel`. Falls back to `fallbackLabel` when the key is
 * unrecognized (e.g. 'upload_failed', which has no STEP_CONFIG entry).
 * @param {string} currentStepKey
 * @param {string} [fallbackLabel]
 * @returns {string}
 */
export function getErrorLabel(currentStepKey, fallbackLabel = '') {
    return STEP_CONFIG.find((step) => step.key === currentStepKey)?.errorLabel || fallbackLabel;
}

const INITIAL_PROGRESS_STATE = {
    percentage: 0,
    currentStepLabel: '',
    currentStepKey: '',
    hasStepError: false,
    hasBackendError: false,
    isErrorResolved: false,
    isProgressResolved: false,
    needsCorrection: false,
    isComplete: false,
    errorType: 'processing'
};

const OFFLINE_COMPLETE_PROGRESS_STATE = {
    ...INITIAL_PROGRESS_STATE,
    percentage: 100,
    currentStepLabel: 'Process complete',
    isErrorResolved: true,
    isProgressResolved: true,
    isComplete: true,
};

/**
 * Build a normalized progress state for a backend progress snapshot.
 * If step is missing, use explicit defaults instead of carrying stale values.
 * @param {PatientProgressData | null} data
 */
function buildProgressStateFromData(data) {
    const nextState = {
        ...INITIAL_PROGRESS_STATE,
        isProgressResolved: true,
    };

    if (!data) {
        return nextState;
    }

    if (data.step === 'upload_failed') {
        return {
            ...nextState,
            currentStepLabel: 'Upload failed',
            currentStepKey: 'upload_failed',
            hasStepError: true,
            isErrorResolved: true,
        };
    }

    const stepConfig = STEP_CONFIG.find((s) => s.key === data.step);
    const isComplete = data.model_exported === true;
    const percentage = stepConfig ? stepConfig.percentage : (isComplete ? 100 : 0);
    const currentStepLabel = stepConfig ? stepConfig.label : (isComplete ? 'Process complete' : '');

    return {
        ...nextState,
        percentage,
        currentStepLabel,
        currentStepKey: data.step || '',
        needsCorrection: data.seg_done === true && data.seg_validated === false,
        isComplete,
    };
}

/**
 * @param {string} patientId
 * @param {boolean} [assumeOfflineComplete=false] if true, treat the patient as fully processed offline
 */
export function usePatientProgress(patientId, assumeOfflineComplete = false) {
    const [progressState, setProgressState] = useState(INITIAL_PROGRESS_STATE);

    useEffect(() => {
        if (!patientId) {
            setProgressState(INITIAL_PROGRESS_STATE);
            return;
        }

        if (assumeOfflineComplete) {
            setProgressState(OFFLINE_COMPLETE_PROGRESS_STATE);
            return;
        }

        setProgressState(INITIAL_PROGRESS_STATE);

        const unsubscribeProgress = subscribeToPatientProgress(patientId, (
            /** @type {PatientProgressData | null} */ data
        ) => {
            setProgressState((currentState) => {
                const built = buildProgressStateFromData(data);
                return {
                    ...built,
                    hasBackendError: currentState.hasBackendError,
                    isErrorResolved: built.isErrorResolved || currentState.isErrorResolved,
                };
            });
        }, (err) => {
            console.error('subscribeToPatientProgress error:', err);
        });

        const unsubscribeError = subscribeToPatientErrors(patientId, (hasError, errorType) => {
            setProgressState((currentState) => ({
                ...currentState,
                hasBackendError: hasError,
                isErrorResolved: true,
                errorType: errorType
            }));
        }, (err) => {
            console.error('subscribeToPatientErrors error:', err?.code, err?.message);
            setProgressState((currentState) => ({
                ...currentState,
                hasBackendError: false,
                isErrorResolved: true,
            }));
        });

        return () => {
            unsubscribeProgress();
            unsubscribeError();
        };
    }, [patientId, assumeOfflineComplete]);

    return {
        percentage: progressState.percentage,
        currentStepLabel: progressState.currentStepLabel,
        currentStepKey: progressState.currentStepKey,
        isError: progressState.hasStepError || progressState.hasBackendError,
        isStatusResolved: progressState.isErrorResolved && progressState.isProgressResolved,
        needsCorrection: progressState.needsCorrection,
        isComplete: progressState.isComplete,
        errorType: progressState.errorType
    };
}